// This file would contain the validation logic for the Reactflow data.
// Global validation for all the node data would be here.
// Using zod for the validation.

import type { Edge } from '@xyflow/react'
import type { RProvider } from 'v2/pages/flow/sidebar/types'
import { handoffContextPolicySchema, qnrSchema } from 'v2/pages/settings/flows/queues/qnr.config'
import { FlowTypes } from 'v2/types'
import { logger } from 'v2/utils/logger'
import { z } from 'zod'
import { DEFAULT_INITIAL_WAIT_TIME } from '../constants'
import { type EdgeData, FlowNodeTypes } from '../types'
import { getNodeVarsFromAncestorEdges, getProviders } from '../utils'
import { SlackChannelTypes } from './trigger-node.config'

// phlo-core call_forward schema: confirm_key allowed values
export const CONFIRM_KEYS = ['#', '*', '1', '0'] as const

const zStringReq = (msg?: string) =>
  z
    .string()
    .trim()
    .min(1, { message: msg ?? 'This field is required' })

export const optionalUrl = z.union([
  z.string().url({ message: 'Please enter a valid URL' }),
  z.literal(''),
])

export const baseSchema = z.object({
  name: zStringReq('Name cannot be empty'),
  description: z.string().nullish(),
})

export const refinePrompt = {
  message: 'Text/file/url is required',
  refiner: (data: { url: string; text: string; file: string }) =>
    data.url || data.text || data.file,
}

export const expressionSchema = z.object({
  case_sensitive: z.boolean().default(false),
  lhs: z.string().min(1, "'Value to compare' is required"),
  operation: z.string().min(1, 'Operation is required'),
  rhs: z
    .array(z.string())
    .optional()
    .or(z.array(z.array(z.string())).optional()),
})

export const actionsSchema = z
  .object({
    name: z.string().optional().nullable(),
    is_expanded: z.boolean().default(false).optional(),
    id: z.string(),
    action_type: z.enum(['EXECUTE_ACTION', 'EXTRACT_VARIABLES', 'CUSTOM_CODE']).nullable(),
    variable_name: z
      .string()
      .regex(/^[a-zA-Z_]*$/, 'Variable Name must only contain alphabets and underscores')
      .nullable(),
    variable_instructions: z.string().nullable(),
    org_app_uuid: z.string().nullable().optional(),
    action_name: z.string().nullable(),
    action_instructions: z.string().nullable(),
    code_name: z.string().nullable(),
    code_description: z.string().nullable(),
    code_body: z.string().nullable(),
    app_provider: z.string().nullable().optional(),
    code_function_schema: z.record(z.string(), z.unknown()).nullish(),
    action_inputs: z.record(z.string(), z.string().nullable()).nullish(),
    action_field_types: z
      .object({
        required_fields: z.record(z.string(), z.enum(['auto', 'manual'])).optional(),
        optional_fields: z.record(z.string(), z.enum(['auto', 'manual'])).optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action_type === 'EXECUTE_ACTION') {
      if (!data.action_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Action name is required',
          path: ['action_name'],
        })
      }
      if (!data.action_instructions) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Action Instructions is required',
          path: ['action_instructions'],
        })
      }
    }

    if (data.action_type === 'EXTRACT_VARIABLES') {
      if (!data.variable_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Variable Name is required',
          path: ['variable_name'],
        })
      }
      if (!data.variable_instructions) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Variable Instructions is required',
          path: ['variable_instructions'],
        })
      }
    }

    if (data.action_type === 'CUSTOM_CODE') {
      if (!data.code_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function name is required',
          path: ['code_name'],
        })
      }
      if (!data.code_description) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function description is required',
          path: ['code_description'],
        })
      }
      if (!data.code_body) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Code is required',
          path: ['code_body'],
        })
      }
      if (!data.code_function_schema) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function schema is required. Please generate.',
          path: ['code_function_schema'],
        })
      }
    }
  })

export const actionsArraySchema = z.array(actionsSchema)

// Screening half shared by the standalone node and the outbound composite.
const screeningFieldsSchema = {
  context: z.string().min(1, 'Describe the call — at minimum, who the agent should ask for'),
  ask_permission: z.boolean().default(true),
  initial_wait_time: z
    .number({ message: 'Initial wait time is required' })
    .min(0, 'Wait time must be at least 0 seconds')
    .max(15, 'Wait time must be at most 15 seconds')
    .default(DEFAULT_INITIAL_WAIT_TIME),
  // FE-only: which node the Voicemail port routes to (canvas-derived,
  // never serialized — the wire voicemail intent is always emitted).
  voicemail_detection_next_node: z.string().optional(),
}

export const aiAgentV2ActionSchema = z
  .object({
    name: z.string().optional().nullable(),
    is_expanded: z.boolean().default(false).optional(),
    id: z.string(),
    action_type: z.enum(['EXECUTE_ACTION', 'CUSTOM_CODE', 'HTTP']).nullable(),
    org_app_uuid: z.string().nullable().optional(),
    action_name: z.string().nullable(),
    action_instructions: z.string().nullable(),
    action_schema: z.record(z.string(), z.unknown()).nullish(),
    action_inputs: z.record(z.string(), z.string().nullable()).nullish(),
    action_field_types: z
      .object({
        required_fields: z.record(z.string(), z.enum(['auto', 'manual'])).optional(),
        optional_fields: z.record(z.string(), z.enum(['auto', 'manual'])).optional(),
      })
      .optional(),
    app_provider: z.string().nullable().optional(),
    code_name: z.string().nullable(),
    code_description: z.string().nullable(),
    code_body: z.string().nullable(),
    code_function_schema: z.record(z.string(), z.unknown()).nullish(),
    // HTTP action fields
    http_tool_name: z.string().nullable().optional(),
    http_tool_description: z.string().nullable().optional(),
    http_method: z.enum(['get', 'post', 'put', 'patch', 'delete']).optional(),
    http_url: z.string().nullable().optional(),
    http_auth_type: z.enum(['no_auth', 'basic_auth', 'bearer_token']).optional(),
    http_auth_username: z.string().nullable().optional(),
    http_auth_password: z.string().nullable().optional(),
    http_auth_token: z.string().nullable().optional(),
    http_headers: z.string().nullable().optional(),
    http_params: z.string().nullable().optional(),
    http_body_type: z.enum(['none', 'json', 'form_data', 'form_urlencoded']).optional(),
    http_body_template: z.string().nullable().optional(),
    http_function_schema: z.record(z.string(), z.unknown()).nullish(),
    http_timeout_ms: z.number().min(0).max(30000).optional(),
    http_retry_limit: z.number().min(0).max(5).optional(),
    http_test_response: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action_type === 'EXECUTE_ACTION') {
      if (!data.action_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Action name is required',
          path: ['action_name'],
        })
      }
      if (!data.action_instructions?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Action Instructions is required',
          path: ['action_instructions'],
        })
      }
    }

    if (data.action_type === 'CUSTOM_CODE') {
      if (!data.code_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function name is required',
          path: ['code_name'],
        })
      }
      if (!data.code_description?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function description is required',
          path: ['code_description'],
        })
      }
      if (!data.code_body?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Code is required',
          path: ['code_body'],
        })
      }
      if (!data.code_function_schema) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function schema is required. Please generate.',
          path: ['code_function_schema'],
        })
      }
    }

    if (data.action_type === 'HTTP') {
      if (!data.http_tool_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tool name is required',
          path: ['http_tool_name'],
        })
      }
      if (!data.http_tool_description?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tool description is required',
          path: ['http_tool_description'],
        })
      }
      if (!data.http_url?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'URL is required',
          path: ['http_url'],
        })
      }
      if (!data.http_function_schema) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Function schema is required',
          path: ['http_function_schema'],
        })
      }
    }
  })

const getNormalizedActionName = (action: z.infer<typeof aiAgentV2ActionSchema>) => {
  if (action.action_type === 'EXECUTE_ACTION') {
    return action.action_name?.trim().toLowerCase() || null
  }
  if (action.action_type === 'CUSTOM_CODE') {
    return action.code_name?.trim().toLowerCase() || null
  }
  if (action.action_type === 'HTTP') {
    return action.http_tool_name?.trim().toLowerCase() || null
  }
  return null
}

export const aiAgentV2ActionsArraySchema = z
  .array(aiAgentV2ActionSchema)
  .superRefine((actions, ctx) => {
    const names = new Map<string, number>()
    actions.forEach((action, index) => {
      const name = getNormalizedActionName(action)
      if (name) {
        if (names.has(name)) {
          const field =
            action.action_type === 'EXECUTE_ACTION'
              ? 'action_name'
              : action.action_type === 'HTTP'
                ? 'http_tool_name'
                : 'code_name'
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Action name "${name}" is already used. Names must be unique.`,
            path: [index, field],
          })
        }
        names.set(name, index)
      }
    })
  })

export const baseIntentsSchema = z.object({
  id: z.string(),
  intent_name: z
    .string()
    .regex(
      /^[a-zA-Z\s_']*$/,
      'Name must only contain alphabets, spaces, underscores, and apostrophes.',
    )
    .optional(),
  intent_instructions: z.string().optional(),
})

export const intentSpeechSchema = baseIntentsSchema.extend({
  intent_speech: z
    .object({
      speech_enabled: z.boolean().default(false),
      intent_speech_text: z
        .string()
        .max(100, 'Speech text cannot exceed 100 characters')
        .optional(),
    })
    .default({
      speech_enabled: false,
      intent_speech_text: '',
    }),
})

export const intentsSchema = baseIntentsSchema.superRefine((data, ctx) => {
  if (data.intent_name && !data.intent_instructions) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Intent Instruction is required when Intent Name is provided',
      path: ['intent_instructions'],
    })
  }
  if (!data.intent_name && data.intent_instructions) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Intent Name is required when Intent Instruction is provided',
      path: ['intent_name'],
    })
  }
})

export const nodeConfigSchema = z
  .object({
    intents: z.array(intentsSchema),
  })
  .superRefine((data, ctx) => {
    const intentNames = data.intents.map((intent) => intent.intent_name?.trim()).filter(Boolean)

    const duplicates = intentNames.filter((name, index) => intentNames.indexOf(name) !== index)

    if (duplicates.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate Intent Names(s) found: ${duplicates.join(', ')}.`,
        path: [`${duplicates.join(', ')}`],
      })
    }
  })

export const voiceConfigSchema = z.object({
  voice: z.string().min(1, 'Voice is a required field.'), // Mandatory voice field
  pronunciation: z
    .array(
      z.object({
        word: z
          .string()
          .min(1, 'Word is required')
          .max(50, 'Word cannot exceed 50 characters')
          .optional(),
        guide: z
          .string()
          .min(1, 'Guide is required')
          .max(100, 'Guide cannot exceed 100 characters')
          .optional(),
      }),
    )
    .max(10, 'Maximum of 10 pronunciation entries allowed'),
})

export const nodeVarRegex = /^\{\{[a-zA-Z_][a-zA-Z0-9_.[\]-]*\}\}$/

// Loose match: any {{...}} substring. `allowedVars` is the pre-formatted set
// returned by getAllNodeVarsFromNode / getNodeVarsFromAncestorEdges
// (e.g. `{{Start.from}}`, `{{Start.incoming_call.uuid}}`) — compared verbatim.
const NODE_VAR_REF_REGEX = /\{\{[^}]+\}\}/g

// `{{Secrets.X}}` refs are always valid (resolved at runtime against the
// account's secret store). Mirrors phlo-core's SECRET_VAR_PREFIX bypass in
// `_validate_check_input_vars` and runtime `get_value`.
const SECRET_VAR_REF_PREFIX = '{{Secrets.'

// Returns the unrecognised {{...}} refs in `str`. Used both for the boolean
// predicate (via createNodeVarValidator) and to build informative error
// messages that name the offending refs.
export const findInvalidNodeVarRefs = (str: string, allowedVars: string[]): string[] => {
  if (!str) return []
  const matches = str.match(NODE_VAR_REF_REGEX) || []
  return matches.filter((m) => !m.startsWith(SECRET_VAR_REF_PREFIX) && !allowedVars.includes(m))
}

// Factory yielding a boolean predicate suitable for z.refine. Closed over
// `allowedVars` so callers can build a per-context schema (e.g. ancestor vars
// for AI nodes, Start-only vars for the Global Prompt sidebar).
export const createNodeVarValidator =
  (allowedVars: string[]) =>
  (str: string): boolean =>
    findInvalidNodeVarRefs(str, allowedVars).length === 0

// Reusable URL or node variable validation schema
export const urlOrNodeVarSchema = z.union([
  z.string().url({ message: 'Please enter a valid URL' }),
  z.string().refine((val) => nodeVarRegex.test(val), {
    message: 'String must either be a valid URL or a valid node variable.',
  }),
])

// Optional version of the URL or node variable schema
export const optionalUrlOrNodeVarSchema = urlOrNodeVarSchema.optional()

const messageMediaSchema = z.object({
  media_type: z.string().optional(),
  name: z.string().optional(),
  url: optionalUrlOrNodeVarSchema,
  size: z.number().optional(),
  file_id: z.string().optional(),
})

export const invalidSelectValueSchema = <T>(schema: z.ZodType<T>, options: T[]) =>
  schema.refine((val) => options.includes(val), {
    message: 'Selected value not found in the list of available options',
  })

// Add a base AI agent schema with common fields after all required schemas are defined
// This is for older channel specific agent components, not used in ai agent v2
export const baseAiAgentSchema = baseSchema.extend({
  model_config: z
    .object({
      model: z.string().optional(),
      provider: z.string().optional(),
    })
    .nullable()
    .optional()
    .refine(
      (data) => {
        if (!data?.model) return true
        const providers = getProviders()
        const llmProviders = providers?.llm_providers || []

        const provider = llmProviders.find((p: RProvider) => p.id === data?.provider)
        if (!provider) return false

        return provider.models.some((m: { id: string }) => m.id === data?.model)
      },
      {
        message: 'Selected model does not support the chosen language',
      },
    ),
  // TODO: Made optional temporarily to avoid validation errors
  channel: z.enum(['message', 'whatsapp', 'chat', 'call']),
  conversation_type: z.enum(['ai_generated', 'preset_message']),
  wait_for_response: z.boolean().default(false),
  reply_timeout: z.number().min(60).max(86400).optional(),
  disable_message_type: z.boolean().default(false),
  disable_human_handoff: z.boolean().default(false),
  disable_inequipped_to_serve: z.boolean().optional(),
  message_type: z.enum(['GENERATED', 'STATIC', 'NONE']),
  message_prompt: z.string().nullable(),
  message_text: z.string().nullable(),
  // The real validation is done in the ai-instructions.tsx
  instructions: z.string().optional(),
  instruction_attachments: z.any(),
  no_response_timeout: z.number().optional(),
  completion_criteria: z.string().optional(),
  human_handoff_instructions: z.string().min(1, 'Human handoff instruction is required'),
  no_response_instructions: z.string().optional(),
  return_to_source: z.boolean().optional(),
  actions: actionsArraySchema,
  node_config: nodeConfigSchema,
})

// Common AI configuration schemas for reuse
const baseAiConfigSchema = z.object({
  model_id: z.string({ message: 'Model is required' }),
  provider_id: z.string({ message: 'Provider is required' }),
})

export const imageGenerationConfigSchema = baseAiConfigSchema.extend({
  instructions: zStringReq('Instructions are required'),
})

export const imageAnalysisConfigSchema = baseAiConfigSchema.extend({
  img_url: urlOrNodeVarSchema,
  instructions: zStringReq('Instructions are required'),
})

export const audioTranscriptionConfigSchema = baseAiConfigSchema.extend({
  language: z.string({ message: 'Language is required' }),
  audio_url: urlOrNodeVarSchema,
})

export const generateAudioSchema = baseAiConfigSchema.extend({
  voice_id: zStringReq('Voice is required'),
  text: zStringReq('Text is required'),
  instructions: z.string().optional(),
})

export const textSummarizationConfigSchema = baseAiConfigSchema.extend({
  summary_url: zStringReq('Summary URL is required'),
  instructions: zStringReq('Instructions are required'),
})

// Shared event callback schema for MPC v2 and Conference Bridge
const eventCallbackSchema = z
  .array(
    z.object({
      event_type: z.string().min(1, 'Event type is required'),
      url: z
        .string()
        .optional()
        .refine(
          (val) => !val || z.string().url().safeParse(val).success || nodeVarRegex.test(val),
          { message: 'Must be a valid URL or node variable' },
        ),
      method: z.enum(['GET', 'POST']),
    }),
  )
  .optional()

// IMPORTANT: The keys in this object should match the FlowNodeTypes enum
// This is the important part where we define the validation schema for each node type
export const v2validations = (meta?: {
  nodeVars: string[]
  flowType?: string
  validateHandoffContext?: boolean
}) => {
  const flowType = meta?.flowType ?? window.location.pathname.split('/')[3]
  const nodevarSchema = z.string().refine(createNodeVarValidator(meta?.nodeVars ?? []), {
    message: 'Value contains invalid node variables',
  })

  // Base prompt object schema without refinement
  const promptObjectSchema = z.object({
    delay: z.number({ message: 'Wait time is required' }),
    file: z.string({ message: 'File is required' }),
    language: z.string({ message: 'Language is required' }),
    prompt_speak_type: z.string({ message: 'Prompt speak type is required' }),
    prompt_type: z.string({ message: 'Prompt type is required' }),
    text: nodevarSchema,
    url: z.string({ message: 'Url is required' }),
    voice: z.string({ message: 'Voice is required' }),
  })

  // Extended prompt object schema with loops without refinement
  const promptWithLoopsObjectSchema = promptObjectSchema.extend({
    loops: z.number({ message: 'Number of times to repeat this audio is required' }).max(10),
    silence: z.number().min(0, 'Silence cannot be negative').optional().default(0),
    beep: z.boolean().optional().default(false),
  })

  // Add refinement to base schema
  const promptSchema = promptObjectSchema.refine(refinePrompt.refiner, {
    message: refinePrompt.message,
  })

  // Add refinement to extended schema with loops
  const promptWithLoopsSchema = promptWithLoopsObjectSchema.refine(refinePrompt.refiner, {
    message: refinePrompt.message,
  })

  // The name rule differs per node: V2 extracts into template variables, V2.9 Inputs are
  // runtime identifiers, so the schema takes it as an argument.
  const extractVariablesSchema = (
    variable_name = z
      .string()
      .regex(/^[a-zA-Z_]*$/, 'Variable Name must only contain alphabets and underscores')
      .nullable(),
  ) =>
    z
      .array(
        z.object({
          variable_name,
          variable_instructions: z.string().nullable(),
          configure_data_format: z.boolean().nullable().optional(),
          variable_type: z.enum(['string', 'number', 'boolean', 'enum']).nullable().optional(),
          variable_format_instructions: z.string().nullable().optional(),
          variable_enum_values: z.array(z.string()).nullable().optional(),
          // V2.9 only: the Input's collection configuration, kept verbatim
          collection: z.record(z.unknown()).nullable().optional(),
        }),
      )
      .optional()
      .nullable()

  const validations = {
    promptItem: promptSchema,
    [FlowNodeTypes.start]: baseSchema.extend({
      url: z.string().url().optional(),
      auth_token: z.string().optional(),
      auth_id: z.string().optional(),
      payload_format: z.any().refine(
        (val) => {
          if (val == null || typeof val !== 'object') return true
          const validateKeys = (obj: Record<string, unknown>): boolean =>
            Object.entries(obj).every(([k, v]) => {
              if (!/^[a-zA-Z0-9 _-]+$/.test(k)) return false
              if (v && typeof v === 'object' && !Array.isArray(v))
                return validateKeys(v as Record<string, unknown>)
              return true
            })
          return validateKeys(val)
        },
        { message: 'Payload keys must contain only letters, digits, underscores, and hyphens' },
      ),
      call_hangup_flow: z.string().optional(),
      // Node-level event callbacks (legacy PHLO support, e.g. hangup). Kept in
      // the schema so they survive save instead of being stripped. SER-4772.
      events: eventCallbackSchema,
      require_http_auth: z.boolean().default(true),
      enable_quick_responses: z.boolean().default(false),
      triggers: z.array(z.string()),
      quick_response_questions: z
        .array(
          z.object({
            id: z.string(),
            value: z
              .string()
              .trim()
              .min(1, 'Question cannot be empty')
              .max(65, 'Maximum 65 characters allowed'),
          }),
        )
        .max(5, 'Maximum 5 questions allowed')
        .optional(),
      slack_trigger_event: z.enum([SlackChannelTypes.DIRECT, SlackChannelTypes.MENTION]).optional(),
      slack_bot_token: z.string().optional().nullable(),
      slack_signing_secret: z.string().optional().nullable(),
      slack_channel_id: z.string().optional().nullable(),
      slack_reply_in_thread: z.boolean().optional().nullable(),
    }),
    [FlowNodeTypes.note]: z.object({
      content: z.string().optional(),
      color: z.string().optional(),
    }),
    [FlowNodeTypes.menu]: baseSchema,
    // Multi-Party Call V2 validation schema
    [FlowNodeTypes.multi_party_call_v2]: baseSchema
      .extend({
        from: z.string().min(1, 'Caller ID is required'),
        to: z
          .array(z.string().trim())
          .transform((arr) => arr.filter((s) => s.length > 0))
          .pipe(z.array(z.string().min(1)).min(1, 'At least one destination is required')),
        role: z.enum(['agent', 'customer', 'supervisor']).optional(),
        call_type: z.enum(['agent_dials', 'agent_added']).optional(),
        record: z.boolean().optional(),
        record_mode: z.enum(['always', 'dynamic']).optional(),
        record_condition: z.string().optional(),
        recording_option: z.enum(['all_separate', 'customer_and_agent', 'all_together']).optional(),
        transfer: z.enum(['cold', 'warm']).optional(),
        customer_hold_music: z
          .object({
            url: z.string().optional(),
            file: z.string().optional(),
          })
          .optional(),
        agent_hold_music: z
          .object({
            url: z.string().optional(),
            file: z.string().optional(),
          })
          .optional(),
        voicemail_message: promptObjectSchema.optional(),
        extra_headers: z.string().max(2048, 'SIP headers too long').optional(),
        events: eventCallbackSchema,
      })
      .superRefine((data, ctx) => {
        if (data.record && data.record_mode === 'dynamic' && !data.record_condition?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Condition is required for dynamic recording',
            path: ['record_condition'],
          })
        }
      }),

    // Conference Bridge validation schema
    [FlowNodeTypes.conference_bridge]: baseSchema
      .extend({
        conference_name: z.string().min(1, 'Conference name is required'),
        conference_type: z.enum(['custom', 'pinned']).default('custom'),
        participant_pin: z.string().optional(),
        mod_pin: z.string().optional(),
        pin_entry_tries: z.number().min(1).max(10).optional(),
        record: z.boolean().optional(),
        record_mode: z.enum(['always', 'dynamic']).optional(),
        record_condition: z.string().optional(),
        announcement: promptObjectSchema.optional(),
        hold_prompt: z
          .object({
            url: z.string().optional(),
            file: z.string().optional(),
          })
          .optional(),
        entry_chime: z.enum(['none', 'beep', 'tone']).optional(),
        exit_chime: z.enum(['none', 'beep', 'tone']).optional(),
        join_muted: z.boolean().optional(),
        hold_callers: z.boolean().optional(),
        events: eventCallbackSchema,
      })
      .superRefine((data, ctx) => {
        if (data.conference_type === 'pinned') {
          if (!data.participant_pin?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Participant PIN is required for pinned conferences',
              path: ['participant_pin'],
            })
          }
          if (!data.mod_pin?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Moderator PIN is required for pinned conferences',
              path: ['mod_pin'],
            })
          }
        }
        if (data.record && data.record_mode === 'dynamic' && !data.record_condition?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Condition is required for dynamic recording',
            path: ['record_condition'],
          })
        }
      }),

    [FlowNodeTypes.stream]: baseSchema.extend({
      mode: z.enum(['audio', 'text']), // audio: raw audio to customer, text: Pipecat handles STT/TTS
      websocket_endpoint: z
        .string()
        .trim()
        .min(1, 'WSS Endpoint is required')
        .refine((val) => /^wss:\/\//.test(val), 'WebSocket URL must use wss:// protocol'),
      // Audio mode specific fields
      enable_noise_cancellation: z.boolean().optional(), // Enable noise cancellation (audio mode)
      noise_cancellation_level: z.number().min(60).max(100).default(85).optional(), // Noise cancellation level (60-100)
      // Shared and Text mode specific fields
      enable_recording: z.boolean().optional(), // Enable call recording
      play_greeting: z.boolean().optional(), // Toggle for greeting message
      // first_message is a greeting message played when connection is established (text mode only)
      first_message: z.string().nullable().optional(),
      // first_message_audio_url is the pre-generated TTS audio URL for the greeting message
      first_message_audio_url: z.string().nullable().optional(),
      // stt_config is only used in text mode - Speech-to-Text configuration
      stt_config: z
        .object({
          provider: z.string().min(1, 'STT provider is required'),
          model: z.string().optional(),
          language: z.string().min(1, 'Language is required'),
          turn_detection: z.boolean().optional(), // Auto-detect when caller stops speaking
          live_transcription: z.boolean().optional(), // Stream partial transcripts
          end_of_turn_threshold: z.number().min(0).max(1).optional(), // Confidence threshold (0.0-1.0)
          end_of_turn_timeout: z.number().min(0).optional(), // Max pause in ms before agent responds
          keyterms: z.string().optional(), // Comma-separated keywords for vocabulary expansion
        })
        .nullable()
        .optional(),
      // tts_config is only used in text mode - Text-to-Speech configuration
      tts_config: z
        .object({
          provider: z.string().min(1, 'TTS provider is required'),
          model: z.string().optional(),
          voice_id: z.string().min(1, 'Voice is required'),
          language: z.string().optional(),
          gender: z.enum(['male', 'female']).optional(), // Preferred gender for voice
          speed: z.number().min(0.5).max(2.0).optional(),
          stability: z.number().min(0).max(1).optional(), // For ElevenLabs
          filler_audio: z.string().optional(), // Background audio type: none, typing, ambient
        })
        .nullable()
        .optional(),
      // background_audio is only used in text mode - ambient background sounds
      background_audio: z
        .object({
          type: z.string().default('none'),
          volume: z.number().min(0).max(2).default(1),
          thinking_sound_enabled: z.boolean().default(true),
          thinking_sound_volume: z.number().min(0).max(1).default(0.7),
        })
        .nullable()
        .optional(),
    }),

    [FlowNodeTypes.ai_skills]: baseSchema.extend({
      tool_type: z.string(),
      image_generation_config: imageGenerationConfigSchema.nullable().optional(),
      image_analysis_config: imageAnalysisConfigSchema.nullable().optional(),
      audio_transcription_config: audioTranscriptionConfigSchema.nullable().optional(),
      summary_config: textSummarizationConfigSchema.nullable().optional(),
      text_to_speech_config: generateAudioSchema.nullable().optional(),
    }),

    [FlowNodeTypes.end_conversation]: baseSchema.extend({
      end_message: z.string().optional().nullable(),
    }),

    [FlowNodeTypes.agent_node]: baseSchema.extend({
      channel: z.literal('call').default('call'),
      instructions: z.string().min(1, 'Instructions are required'),
      intents: z.array(intentSpeechSchema).nullable(),
      agent_tasks: z
        .object({
          variables: z.array(
            z
              .object({
                name: z
                  .string()
                  .min(1, 'Variable name is required')
                  .regex(/^[a-z][a-z0-9_]*$/, 'Use lowercase letters, numbers, and underscores'),
                instructions: z.string(),
                type: z
                  .enum([
                    'text',
                    'name',
                    'email',
                    'phone',
                    'choice',
                    'date',
                    'currency',
                    'boolean',
                    'address',
                    'number',
                    'dob',
                    'dtmf',
                  ])
                  .default('text'),
                params: z.record(z.unknown()).optional(),
                optional: z.boolean().optional(),
                known_value: z.string().optional(),
                confirm_if_known: z.boolean().optional(),
              })
              .superRefine((variable, ctx) => {
                if (variable.type !== 'dtmf') {
                  return
                }
                const numDigits = variable.params?.num_digits
                if (
                  typeof numDigits !== 'number' ||
                  !Number.isInteger(numDigits) ||
                  numDigits < 1
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['params', 'num_digits'],
                    message: 'Number of digits is required and must be a positive whole number',
                  })
                }
              }),
          ),
          extract_only: z
            .array(
              z.object({
                name: z
                  .string()
                  .min(1, 'Variable name is required')
                  .regex(/^[a-z][a-z0-9_]*$/, 'Use lowercase letters, numbers, and underscores'),
                instructions: z.string(),
              }),
            )
            .default([]),
          confirm_announcement: z.string().optional(),
          max_restarts: z.number().optional(),
        })
        .nullable()
        .optional(),
      model_config: z
        .object({
          model: z.string().optional(),
          provider: z.string().optional(),
        })
        .nullable()
        .optional(),
      actions: aiAgentV2ActionsArraySchema.optional(),
      initial_wait_time: z
        .number({ message: 'Initial wait time is required' })
        .min(0, 'Wait time must be at least 0 seconds')
        .max(15, 'Wait time must be at most 15 seconds')
        .default(DEFAULT_INITIAL_WAIT_TIME),
      allow_greeting_interruption: z.boolean().optional(),
    }),

    [FlowNodeTypes.ai_agent_v2]: baseSchema.extend({
      model_config: z
        .object({
          model: z.string().optional(),
          provider: z.string().optional(),
        })
        .nullable()
        .optional(),
      channel: z.enum(['message', 'whatsapp', 'chat', 'call', 'slack']),
      instructions: z.string().min(1, 'Instructions are required'),
      intents: z.array(intentSpeechSchema).nullable(),
      extract_variables: extractVariablesSchema(),
      actions: aiAgentV2ActionsArraySchema.optional(),
      update_similar_nodes: z.boolean().optional(),
      voicemail_detection_next_node: z.string().optional(),
      // FE-only sibling of voicemail_detection_next_node for chat: which node
      // the AI node's "no reply" (reply-timeout) port routes to.
      no_reply_next_node: z.string().optional(),
      screening_name: z.string().nullable().optional(),
      screening_reason: z.string().nullable().optional(),
      initial_wait_time: z
        .number({ message: 'Initial wait time is required' })
        .min(0, 'Wait time must be at least 0 seconds')
        .max(15, 'Wait time must be at most 15 seconds')
        .default(DEFAULT_INITIAL_WAIT_TIME),
      allow_greeting_interruption: z.boolean().optional(),
      wait_for_response: z.boolean().default(false),
      reply_timeout: z.number().min(60).max(86400).optional(),
      // Chat only: the AI opens the conversation itself when the flow enters
      // this node with no customer message pending (e.g. returning from a
      // human agent). Unchecked/absent = the AI waits for the customer.
      ai_speaks_first: z.boolean().optional(),
    }),
    // Real wire component (SER-5741): builder fields are the wire fields
    // minus channel; serialized directly in data-transformer.
    [FlowNodeTypes.contact_screening]: baseSchema.extend(screeningFieldsSchema),
    // ONE builder node = initiate_call + contact_screening on the wire — see
    // config/outbound-screening.config.ts (expand/collapse passes).
    [FlowNodeTypes.outbound_screening]: baseSchema.extend({
      events: eventCallbackSchema, // dial-half event callbacks, same as initiate_call
      from: z.string().optional(),
      to: z.string().min(7, 'To number must be at least 7 characters'),
      is_validate_caller_id: z.boolean().default(true),
      ring_timeout: z
        .number()
        .min(1, 'Ring timeout is required')
        .max(60, 'Ring timeout must be at most 60 seconds')
        .default(30),
      max_call_duration: z
        .number()
        .min(1, 'Duration must be at least 1 minute')
        .max(240, 'Duration must be at most 240 minutes')
        .default(10),
      ...screeningFieldsSchema,
    }),
    [FlowNodeTypes.ai_action]: baseSchema.extend({
      actions: actionsArraySchema,
      intents: z.array(intentsSchema),
    }),
    [FlowNodeTypes.select_trigger]: baseSchema,
    [FlowNodeTypes.initiate_call]: baseSchema.extend({
      name: zStringReq('Name is required'),
      events: eventCallbackSchema, // node-level event callbacks (SER-4772)
      from: z.string().optional(),
      to: z.string().min(7, 'To number must be at least 7 characters'),
      is_validate_caller_id: z.boolean().default(true),
      extra_headers: z.string().optional(),
      send_digits: z.string().optional(),
      ring_timeout: z
        .number()
        .min(1, 'Ring timeout is required')
        .max(60, 'Ring timeout must be at most 60 seconds'),
      max_call_duration: z
        .number()
        .min(1, 'Duration must be at least 1 minute')
        .max(240, 'Duration must be at most 240 minutes')
        .optional()
        .default(10)
        .transform((minutes) => minutes * 60), // Convert minutes to seconds for backend
      detect_voicemail: z.boolean().default(false),
    }),

    [FlowNodeTypes.ai_assist]: baseSchema, // NODE_GEN: extend if needed

    [FlowNodeTypes.user_input]: baseSchema.extend({
      contact_identifier: z.boolean().default(false),
      input_validation: z.enum([
        'any',
        'alphanumeric',
        'email',
        'integer',
        'phone_number',
        'url',
        'date',
      ]),
      optional: z.boolean().default(true),
      placeholder_text: z.string().optional(),
      prompt: z.string().min(1, 'Prompt is required'),
      reply_timeout: z
        .number({ message: 'Reply timeout is required' })
        .min(60)
        .max(86400)
        .default(3600),
    }), // NODE_GEN: extend if needed

    [FlowNodeTypes.ai_agent_call]: baseAiAgentSchema,
    [FlowNodeTypes.ai_agent_message]: baseAiAgentSchema.extend({
      from: z.string().nullish(),
      to: z.string().nullish(),
      text: z.string().optional(),
      message_media: z.array(messageMediaSchema).max(10).min(0).optional(),
    }),
    [FlowNodeTypes.ai_agent_chat]: baseAiAgentSchema.extend({
      content_type: z.enum(['text', 'chat_media', 'chat_button']),
      text: z.string().optional(),
      chat_media: messageMediaSchema.optional(),
      header_media: messageMediaSchema.optional(),
      title: z.string().max(100, 'Title cannot exceed 100 characters').optional(),
      button_description: z
        .string()
        .max(1024, 'Button description cannot exceed 1024 characters')
        .optional(),
      buttons: z
        .array(
          z.object({
            id: z.string(),
            button_type: z.enum(['call', 'reply', 'website']),
            text: z.string().max(100, 'Button text cannot exceed 100 characters'),
            call_phone: z
              .string()
              .optional()
              .superRefine((val, ctx) => {
                const buttonType = (ctx.path[ctx.path.length - 2] as { button_type?: string })
                  ?.button_type

                if (buttonType !== 'call') {
                  return
                }

                if (!val) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Phone number is required',
                  })
                  return
                }

                // E.164 format validation
                if (!/^\+?[1-9]\d{1,14}$/.test(val)) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid phone number format',
                  })
                }
              }),
            website_url: z
              .string()
              .optional()
              .superRefine((val, ctx) => {
                const buttonType = (ctx.path[ctx.path.length - 2] as { button_type?: string })
                  ?.button_type

                if (buttonType !== 'website') {
                  return
                }

                if (!val) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Website URL is required',
                  })
                  return
                }

                const isValidUrl = val.startsWith('http://') || val.startsWith('https://')
                if (!isValidUrl) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'URL must start with http:// or https://',
                  })
                }
              }),
          }),
        )
        .min(1, 'At least one button is required')
        .max(5, 'Maximum 5 buttons allowed')
        .optional(),
    }),

    [FlowNodeTypes.ai_agent_whatsapp]: baseAiAgentSchema.extend({
      // nullish is used to allow the field to be null or undefined
      // undefined, because we want the field to be optional and delete the field when the conversation_type is ai_generated
      // null, because we want the field to be nullable, so that when we try to publish a node with the conversation_type as ai_generated, the field is not required and validation passes
      from: z.string().nullish(),
      to: z.string().nullish(),
      text: z.string().optional(),
      whatsapp_media: z.array(messageMediaSchema).max(10).min(0),
      whatsapp_msg_type: z.enum([
        'text',
        'whatsapp_media',
        'whatsapp_button',
        'template',
        'whatsapp_list',
      ]),
      header_type: z.enum(['text', 'media', 'none']).optional(),
      header_text: z.string().max(60, 'Header text cannot exceed 60 characters').optional(),
      header_media: messageMediaSchema.nullish(),
      body: z.string().max(1024, 'Body cannot exceed 1024 characters').optional(),
      footer: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
      whatsapp_list: z
        .object({
          header: z.string().max(60, 'Header text cannot exceed 60 characters').optional(),
          body: z.string().max(1024, 'Body cannot exceed 1024 characters').optional(),
          footer: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
          button: z.string().max(20, 'Menu button text cannot exceed 20 characters').optional(),
        })
        .optional(),
      sections: z
        .array(
          z.object({
            title: z.string().max(24, 'Section title cannot exceed 24 characters').optional(),
            rows: z
              .array(
                z.object({
                  title: z.string().max(24, 'Item title cannot exceed 24 characters'),
                  description: z
                    .string()
                    .max(72, 'Description cannot exceed 72 characters')
                    .optional(),
                  id: z.string(),
                }),
              )
              .min(1, 'At least one item is required')
              .max(10, 'Maximum 10 items allowed'),
          }),
        )
        .max(10, 'Maximum 10 sections allowed')
        .superRefine((sections, ctx) => {
          const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0)
          if (totalRows > 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Total number of rows across all sections cannot exceed 10.',
              path: [],
            })
          }
        })
        .optional(),

      buttons: z
        .array(
          z.object({
            title: z.string().max(25, 'Button text cannot exceed 25 characters'),
            id: z.string(),
          }),
        )
        .max(3, 'Maximum 3 buttons allowed')
        .optional(),
      whatsapp_template: z.any().optional(),
      variables: z.object({
        header: z.array(z.string()).min(0),
        body: z.array(z.string()).min(0),
      }),
      template: z.any().optional(),
    }),

    [FlowNodeTypes.agent_preset_conversation]: baseSchema
      .extend({
        // Common fields
        channel: z.enum(['message', 'whatsapp', 'chat']),
        conversation_type: z.literal('preset_message'),
        wait_for_response: z.boolean().default(false),
        reply_timeout: z.number().min(60).max(86400).optional(),
        update_similar_nodes: z.boolean().optional(),

        // Message channel specific fields
        from: z.string().optional(),
        to: z.string().optional(),
        text: z.string().optional(),
        message_media: z.array(messageMediaSchema).max(10).min(0).optional(),
        events: eventCallbackSchema, // SMS message-status callbacks (parity with send_message)

        // WhatsApp channel specific fields
        whatsapp_media: z.array(messageMediaSchema).max(10).min(0).optional(),
        whatsapp_msg_type: z
          .enum(['text', 'whatsapp_media', 'whatsapp_button', 'template', 'whatsapp_list'])
          .optional(),
        header_type: z.enum(['text', 'media', 'none']).optional(),
        header_text: z.string().max(60, 'Header text cannot exceed 60 characters').optional(),
        header_media: messageMediaSchema.nullish(),
        body: z.string().max(1024, 'Body cannot exceed 1024 characters').optional(),
        footer: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
        whatsapp_list: z
          .object({
            header: z.string().max(60, 'Header text cannot exceed 60 characters').optional(),
            body: z.string().max(1024, 'Body cannot exceed 1024 characters').optional(),
            footer: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
            button: z.string().max(20, 'Menu button text cannot exceed 20 characters').optional(),
          })
          .optional(),
        sections: z
          .array(
            z.object({
              title: z.string().max(24, 'Section title cannot exceed 24 characters').optional(),
              rows: z
                .array(
                  z.object({
                    title: z.string().max(24, 'Item title cannot exceed 24 characters'),
                    description: z
                      .string()
                      .max(72, 'Description cannot exceed 72 characters')
                      .optional(),
                    id: z.string(),
                  }),
                )
                .min(1, 'At least one item is required')
                .max(10, 'Maximum 10 items allowed'),
            }),
          )
          .max(10, 'Maximum 10 sections allowed')
          .superRefine((sections, ctx) => {
            const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0)
            if (totalRows > 10) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Total number of rows across all sections cannot exceed 10.',
                path: [],
              })
            }
          })
          .optional(),
        buttons: z
          .array(
            z.object({
              id: z.string(),
              // WhatsApp button fields
              title: z.string().max(25, 'Button text cannot exceed 25 characters').optional(),
              // Chat button fields
              button_type: z.enum(['call', 'reply', 'website']).optional(),
              text: z.string().max(100, 'Button text cannot exceed 100 characters').optional(),
              call_phone: z
                .string()
                .optional()
                .superRefine((val, ctx) => {
                  const button = ctx.path[ctx.path.length - 2] as { button_type?: string }
                  if (button?.button_type === 'call' && !val) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Phone number is required for call buttons',
                    })
                  }
                  if (button?.button_type === 'call' && val && !/^\+?[1-9]\d{1,14}$/.test(val)) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Invalid phone number format',
                    })
                  }
                }),
              website_url: z
                .string()
                .optional()
                .superRefine((val, ctx) => {
                  const button = ctx.path[ctx.path.length - 2] as { button_type?: string }
                  if (button?.button_type === 'website' && !val) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Website URL is required for website buttons',
                    })
                  }
                  if (button?.button_type === 'website' && val && !val.startsWith('http')) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'URL must start with http:// or https://',
                    })
                  }
                }),
            }),
          )
          .max(5, 'Maximum 5 buttons allowed')
          .optional(),
        variables: z
          .object({
            header: z.array(z.string()).min(0),
            body: z.array(z.string()).min(0),
          })
          .optional(),
        template: z.any().optional(),

        // Chat channel specific fields
        content_type: z.enum(['text', 'chat_media', 'chat_button']).optional(),
        chat_media: messageMediaSchema.optional(),
        title: z.string().max(100, 'Title cannot exceed 100 characters').optional(),
        button_description: z
          .string()
          .max(1024, 'Button description cannot exceed 1024 characters')
          .optional(),
      })
      .superRefine((data, ctx) => {
        // Channel-based validation
        if (data.channel === 'message') {
          // Message channel validation
          if (!data.from) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'From is required for message channel',
              path: ['from'],
            })
          }
          if (!data.to) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'To is required for message channel',
              path: ['to'],
            })
          }
          if (!data.text && (!data.message_media || data.message_media.length === 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Either text or media is required for message channel',
              path: ['text'],
            })
          }
          // Ensure no WhatsApp/Chat specific fields are present
          if (data.whatsapp_msg_type || data.sections || data.content_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid fields for message channel',
              path: ['channel'],
            })
          }
        } else if (data.channel === 'whatsapp') {
          // WhatsApp channel validation
          if (!data.from) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'From is required for WhatsApp channel',
              path: ['from'],
            })
          }
          if (!data.to) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'To is required for WhatsApp channel',
              path: ['to'],
            })
          }
          // Ensure no Message/Chat specific fields are present
          if (data.message_media?.length || data.content_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid fields for WhatsApp channel',
              path: ['channel'],
            })
          }
        } else if (data.channel === 'chat') {
          // Chat channel validation
          if (data.content_type === 'text' && !data.text) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Text is required for chat text content',
              path: ['text'],
            })
          }
          if (data.content_type === 'chat_button' && !data.title) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Title is required for chat button content',
              path: ['title'],
            })
          }
          // Ensure no Message/WhatsApp specific fields are present
          if (data.message_media?.length || data.whatsapp_media?.length || data.whatsapp_msg_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid fields for chat channel',
              path: ['channel'],
            })
          }
        }
      }),

    [FlowNodeTypes.branch_v2]: baseSchema.extend({
      conditions: z
        .array(
          z.object({
            alias: z.string().min(1, 'Alias is required'),
            expression_operators: z.array(z.string()).min(0),
            expressions: z.array(expressionSchema),
          }),
        )
        .min(1, 'At least one condition is required'),
    }), // NODE_GEN: extend if needed

    [FlowNodeTypes.send_whatsapp]: baseSchema.extend({
      from: z.string().min(1, 'From value cannot be empty'),
      to: z.string().min(1, 'To value cannot be empty'),
      text: z.string().optional(),
      whatsapp_media: z.array(messageMediaSchema).max(10).min(0),
      whatsapp_msg_type: z.enum(['custom', 'template']),
      whatsapp_template: z.any().optional(),
      variables: z.object({
        header: z.array(z.string()).min(0),
        body: z.array(z.string()).min(0),
      }),
      template: z.any().optional(),
    }), // NODE_GEN: extend if needed

    [FlowNodeTypes.send_chat]: baseSchema.extend({
      chat_content_type: z.enum(['text', 'chat_media']),
      text: z.string().optional(),
      chat_media: messageMediaSchema.optional(),
    }),

    // NODE_GEN: extend if needed

    [FlowNodeTypes.send_message]: baseSchema.extend({
      from: z.string().min(1, 'From value cannot be empty'),
      to: z.string().min(1, 'To value cannot be empty'),
      message_media: z.array(messageMediaSchema).max(10).min(0),
      text: z.string().optional(),
      wait_for_response: z.boolean().optional(),
      reply_timeout: z
        .number({ message: 'Please enter a valid number' })
        .min(60)
        .max(259200)
        .default(3600)
        .optional(),
      events: eventCallbackSchema, // node-level event callbacks (SER-4772)
    }), // NODE_GEN: extend if needed
    [FlowNodeTypes.multi_party_call_outbound]: baseSchema.extend({
      agent_hold_music: z.any(),
      customer_hold_music: z.any(),
    }), // NODE_GEN: extend if needed

    [FlowNodeTypes.whatsapp_conversation]: baseSchema, // NODE_GEN: extend if needed

    [FlowNodeTypes.message_conversation]: baseSchema, // NODE_GEN: extend if needed

    [FlowNodeTypes.business_hour]: baseSchema.extend({
      business_hrs: z.any(),
      output_states: z.optional(z.any()),
    }),
    // NODE_GEN: extend if needed

    [FlowNodeTypes.app_action]: baseSchema.extend({
      actions: zStringReq('Actions cannot be empty'),
    }),
    // NODE_GEN: extend if needed
    [FlowNodeTypes.http_request]: baseSchema.extend({
      http_request: z
        .object({
          name: z.string(),
          auth_params: z
            .object({
              username: z.string().optional(),
              password: z.string().optional(),
              token: z.string().optional(),
            })
            .optional(),
          auth_type: z.enum(['no_auth', 'basic_auth', 'bearer_token', 'oauth2']),
          body: z.string().optional(),
          body_type: z.enum(['none', 'form_data', 'json', 'form_urlencoded']),
          headers: z.string().optional(),
          method: z.enum(['get', 'post', 'put', 'delete', 'patch']),
          oauth2_details: z.object({
            uuid: z.string(),
          }),
          params: z.string().optional(),
          response_format: z.string().optional(),
          response_type: z.string().optional(),
          retry_limit: z.number({ message: 'Retry limit is required' }).max(3).min(0),
          settings: z.any().nullable(),
          timeout_ms: z.number({ message: 'Timeout is required' }).max(30000).min(0),
          url: urlOrNodeVarSchema,
        })
        .superRefine((data, ctx) => {
          // Check if any dependent fields have been modified
          const hasUrl = data.url && data.url.trim() !== ''
          const hasAuth = data.auth_type && data.auth_type !== 'no_auth'
          const hasParams = data.params && data.params.trim() !== '{}' && data.params.trim() !== ''
          const hasHeaders =
            data.headers && data.headers.trim() !== '{}' && data.headers.trim() !== ''
          const hasBody =
            data.body_type && data.body_type !== 'none' && data.body && data.body.trim() !== ''

          // If any dependent fields are configured, response_format should be tested
          const hasDependentFields = hasUrl || hasAuth || hasParams || hasHeaders || hasBody

          if (
            hasDependentFields &&
            (!data.response_format ||
              data.response_format.trim() === '' ||
              data.response_format === '{}')
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please test the API response before saving',
              path: ['response_format'],
            })
          }
        }),
    }),

    [FlowNodeTypes.create_task]: baseSchema.extend({
      create_task_queue_type: z.enum(['first_in_queue', 'last_in_queue']),
      create_task_type: z.enum(['Follow_up', 'Internal']),
      channel: z.enum(['call', 'message', 'whatsapp']).nullable(),
      due_date: z.object({
        due_date_time_value: z.number({ message: 'Required' }).min(1, 'Due in must be at least 1'),
        due_date_time_unit: z.enum(['hours', 'days']),
      }),
      priority: z.enum(['Low', 'Medium', 'High', 'Critical']),
      message: zStringReq('Message cannot be empty'),
      prompt: z.any().optional(),
    }),

    [FlowNodeTypes.counter]: baseSchema.extend({
      max_count: z.number({ message: 'Required' }).min(1),
    }),

    // Assign
    [FlowNodeTypes.queue_and_route]: baseSchema.merge(
      meta?.validateHandoffContext === false
        ? qnrSchema.extend({ context: handoffContextPolicySchema.optional() })
        : qnrSchema,
    ),

    // Customer Feedback
    [FlowNodeTypes.customer_feedback]: baseSchema.extend({
      input_timeout: z
        .number({ message: 'Required' })
        .min(5, 'Input timeout must be at least 5 seconds'),
      interrupt: z.boolean(),
      delay: z
        .number({ message: 'Wait time is required' })
        .nonnegative('Wait time cannot be negative'),
      prompt: flowType === FlowTypes.incoming_call ? promptSchema : z.any(),
      message: z.string({ message: 'This field is required' }).optional(),
    }),
    // Call Forward
    [FlowNodeTypes.call_forward]: baseSchema.extend({
      from: nodevarSchema.pipe(zStringReq('Please enter a valid Caller ID')),
      to: nodevarSchema.pipe(zStringReq('Please enter a valid number')),
      is_validate_caller_id: z.boolean().default(true),
      // node-level recording flag; only surfaced for legacy/migrated flows
      record: z.boolean().optional(),
      extra_headers: z.string().optional(),
      ring_timeout: z
        .number({ message: 'Required' })
        .min(1, 'Ring timeout must be at least 1 second')
        .max(30, 'Ring timeout must be at most 30 seconds'),
      // Call screening (SER-6328). nullish, not optional: phlo-core GET meta
      // emits null for a rendering_order key the saved model lacks.
      confirm_sound: z.boolean().nullish(),
      confirm_key: z.enum(CONFIRM_KEYS).nullish(),
      confirm_prompt: z
        .object({
          text: z.string().nullish(),
          language: z.string().nullish(),
          voice: z.string().nullish(),
          prompt_speak_type: z.string().nullish(),
        })
        .nullish(),
      events: eventCallbackSchema, // node-level event callbacks (SER-4772)
    }), // NODE_GEN: extend if needed

    // Get Input
    [FlowNodeTypes.get_input]: baseSchema.extend({
      handoff_context_reference: z.boolean().optional(),
      input_timeout: z
        .number({ message: 'Required' })
        .min(1, 'Input timeout must be at least 1 second'),
      interrupt: z.boolean(),
      max_input_size: z.number({ message: 'Required' }).min(1, 'Max input size must be at least 1'),
      prompt: z.array(promptWithLoopsSchema).min(1),
      events: eventCallbackSchema, // node-level event callbacks (SER-4772)
    }),
    // Record Voicemail
    [FlowNodeTypes.voicemail]: baseSchema.extend({
      prompt: promptSchema,
      end_recording: z.string({ message: 'End recording is required' }),
      play_beep: z.boolean(),
      transcription_type: z.enum(['auto', 'none', 'hybrid']),
      transcription_url: optionalUrl,
      voicemail_length: z
        .number({ message: 'Voicemail length is required' })
        .min(2, 'Voicemail length must be at least 2 seconds')
        .max(3600, 'Voicemail length must be at most 3600 seconds'),
      voicemail_timeout: z
        .number({ message: 'Voicemail timeout is required' })
        .min(5, 'Voicemail timeout must be at least 5 seconds')
        .max(3600, 'Voicemail timeout must be at most 3600 seconds'),
      name: z.string().min(1, 'Required'),
    }),
    // Play Audio
    [FlowNodeTypes.prompt]: baseSchema.extend({
      prompt: z.array(promptWithLoopsSchema).min(1),
    }),
    // Hangup
    [FlowNodeTypes.hangup]: baseSchema.extend({
      reason: z.enum(['busy', 'rejected']),
    }),
    // IVR Menu
    [FlowNodeTypes.ivr_menu]: baseSchema.extend({
      allowed_inputs: z.array(z.string()).min(1, 'At least one input is required'),
      input_timeout: z.number({ message: 'Required' }).min(1),
      interrupt: z.boolean(),
      digit_timeout: z.number({ message: 'Required' }).min(1),
      num_digits: z.number({ message: 'Required' }).min(1).max(99),
      prompt: z.array(promptSchema).min(1),
      events: eventCallbackSchema, // node-level event callbacks (SER-4772)
    }),
  }
  // ai_agent_v29 is the V2 node except where the V2.9 runner differs: it is voice-only and
  // an Input name is a runtime identifier, so it may carry digits.
  return {
    ...validations,
    [FlowNodeTypes.ai_agent_v29]: validations[FlowNodeTypes.ai_agent_v2].extend({
      channel: z.literal('call').default('call'), // voice-only, same as agent_node
      extract_variables: extractVariablesSchema(
        z
          .string()
          .regex(
            /^[a-zA-Z][a-zA-Z0-9_]*$/,
            'Input name must start with a letter and contain only letters, digits and underscores',
          )
          .nullable(),
      ),
    }),
  }
}

export const nodesSchema = (meta?: { edges: Edge<EdgeData>[]; validateHandoffContext?: boolean }) =>
  z
    .array(
      z
        .object({
          id: z.string(),
          type: z.nativeEnum(FlowNodeTypes),
          data: z.any(),
        })
        .refine(
          (data) => {
            // Feature: Nodevars
            const nodeVars = getNodeVarsFromAncestorEdges(data.id, meta?.edges || [])
            // To see the actual error replace safeParse with parse

            const validationResult = v2validations({
              nodeVars,
              validateHandoffContext: meta?.validateHandoffContext,
            })[data.type].safeParse(data.data.config)
            logger.debug('validationResult', validationResult)
            return validationResult.success
          },
          (data) => ({
            message: `Invalid data for ${data.data.config.name} component found.`,
            params: { data },
          }),
        ),
    )
    .refine(
      // check for duplicate names
      (data) => {
        const names = data
          .filter((node) => node.type !== FlowNodeTypes.note)
          .map((node) => node.data.config.name)
        return new Set(names).size === names.length
      },
      { message: 'Node names must be unique. Check your node names.' },
    )

const checker = () => {
  // a simple checker to let the developer know if they have missed any node type
  const keys = Object.keys(v2validations())
  const types = Object.values(FlowNodeTypes)
  if (keys.length !== types.length) {
    const missing = types.filter((type) => !keys.includes(type))
    if (missing.length > 0) {
      logger.error('Some node types are missing validation schema', missing)
    }
  }
}

checker()
